import React, { Component } from 'react';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import cuid from 'cuid';
import { get } from 'lodash-es';
import isEqual from 'react-fast-compare';

import SortableImage from './SortableImage';
import getLotExtension from '../Step4/getLotExtension';
import { imagePath, isProd } from '../../../utilities/UrlPaths';
import LotImageErrored from './LotImageErrored';
import LotImageUploading from './LotImageUploading';

// 10 MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const NO_IMAGE = `${imagePath}/images/NoImage.png`;

// Default Axios requests to retry 3 additional times if they fail
axiosRetry(axios, {
  retries: 3,
  retryDelay: () => 3000
});

/**
 * Each file has its own progress state.  As soon as this file is created - we
 * mark it as uploading and it enters the queue.  Upon successful processing we mark it as
 * complete.
 *
 * If the file has an error - we show text underneath and give a red border.
 */
class AddLotImage extends Component {
  constructor(props) {
    super(props);
    // Cancel Token provides a means for a user to cancel the CPS request for image uploading
    this.cancelToken = null;
    /**
     * xhr represents the XMLHTTPRequest - this needs to have larger scope so
     * that we can 'abort' it if a user clicks cancel upload
     */
    this.xhr = null;
    this.remainingAwsRetries = 5;

    this.state = {
      file: props.file,
      hasError: false,
      imageS3URL: null,
      imageThumbnail: null,
      imageUploadedSrc: props.imageUploadedSrc,
      index: props.index,
      isUploading: false,
      progress: 0,
      showSizeWarning: false,
      handleImageSizeCheck: props.handleImageSizeCheck
    };
    this.props = props;
  }

  async componentDidMount() {
    const { imageUploaded } = this.props;

    // If we already uploaded the image - don't do anything here
    if (imageUploaded) {
      return;
    }

    const { addFileError, file, index, lotNumber, maxSeqNumber } = this.props;

    // Step 1 - If the file already has an error - meaning it was rejected by dropzone - mark done!
    if (file.hasError) {
      return;
    }

    // Step 2 - Test file size
    if (file.size > MAX_FILE_SIZE) {
      addFileError('size', index);
      this.setState({ isUploading: false });
      return;
    }

    // Step 3 - Test the file's image dimensions (this loads the thumbnail)
    //  COMMENT OUT this test for now
    /*
    try {
      await this.testImageDimensions(file);
    } catch (e) {
      addFileError(e.message, index);
      this.setState({ isUploading: false });
      return;
    }
    */
    //  this instead that allows small images and shows a warning
    try {
      let smallImageCheck = false;
      const imageTest = new Image();
      imageTest.src = URL.createObjectURL(file);
      imageTest.onload = () => {
        if (imageTest.naturalWidth < 1000 || imageTest.naturalHeight < 1000) {
          smallImageCheck = true;
        }
        this.props.handleImageSizeCheck(smallImageCheck);
      };
    } catch (e) {
      console.log(e);
    }

    //  add this here since it's a part of TestImageDimensions, which we're removing
    const imageThumbnail = URL.createObjectURL(file);
    const imageTest = new Image();
    imageTest.src = imageThumbnail;
    // Combine the upload 'start' here to reduce the total number of renders
    this.setState({ isUploading: true, imageThumbnail });

    /**
     * In this case (Add/Edit) we just want to add the new images to the end
     *
     * If the image is the first one - imageSeq should be 0
     *
     * Otherwise get the max sequence number which already exists, add the index and +1
     */
    const imageSeq = index === 0 && maxSeqNumber === -1 ? 0 : 1 + index + maxSeqNumber;

    const { lotNumber: lotNum, lotExt } = getLotExtension(lotNumber);

    this.uploadFile(imageSeq, lotExt, lotNum);
  }

  uploadFile = async (imageSeq, lotExt, lotNum) => {
    try {
      const { catalogID, handleFileComplete, houseID, lotData } = this.props;

      /**
       * (dyouberg) Safari is dumb and caches GET requests, we need to
       * ensure different requests are being made, or else the incorrect
       * image may be uploaded for several thumbnails.
       *
       * https://github.com/ericelliott/cuid
       */
      const uniqueIdentifier = cuid();

      // Step 1 - Get the S3 Bucket URL
      const s3BucketUrl = `https://9ctqzg3xxj.execute-api.us-east-1.amazonaws.com/${
        isProd ? 'prod' : 'stage'
      }/INV-ImageUploader?numImages=0&bucket=/house/${houseID}/catalog/${catalogID}&nocache=${uniqueIdentifier}`;

      const awsUrlResponse = await axios(s3BucketUrl, {
        'axios-retry': {
          retries: 3,
          retryDelay: () => 3000
        }
      });
      const imageURL = get(awsUrlResponse, 'data[0].imageUrl', '');
      const awsPutUrl = get(awsUrlResponse, 'data[0].putUrl', '');

      /**
       * Step 2 - PUT the file to the S3 Bucket URL using the putURL - see function below
       * for the XMLHttpRequest handling
       */
      await this.handlePutRequest(awsPutUrl, imageSeq, lotExt, lotNum);

      // Step 3 - PUT to CPS using the imageURL
      const cpsPutUrl = `/cps/app/image/house/${houseID}/catalog/${catalogID}?fromDIY=true&doValidation=false`;
      const cpsPutData = [{
        imageSeq,
        imageURL,
        lotExt: lotData.lotExt,
        lotNum: lotData.lotNumber,
        invNumber: lotData.invNumber
      }];

      /**
       *  Instantiate our cancel token so that we can call it with
       * this.cancelToken.cancel() on an abort
       */
      this.cancelToken = axios.CancelToken.source();

      /**
       * In the event of CPS failing - retry the request with exponential
       * delay added in between attempts
       */
      const cpsPutResponse = await axios.put(cpsPutUrl, cpsPutData, {
        cancelToken: this.cancelToken.token,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      });

      const cpsPutResponseError = get(cpsPutResponse, 'data.data.images[0].error', null);
      if (!cpsPutResponseError) {
        const imageS3URL = get(cpsPutResponse, 'data.data.images[0].imageS3URL', null);
        // Signal file is complete to update overall progress
        handleFileComplete(imageS3URL, imageSeq);
        this.setState({ imageS3URL, isUploading: false });
      } else {
        const { addFileError, index } = this.props;
        if (cpsPutResponseError === 'Invalid Image') {
          addFileError('invalidImage', index);
        } else {
          addFileError('timeout', index);
        }

        // Mark file as complete to update state for the file component
        this.setState({ isUploading: false });
      }
    } catch (e) {
      /**
       * A promise was rejected and the file was not uploaded - we use addFileError to display
       * the appropriate error message to the user
       */
      const { addFileError, index } = this.props;
      let errorMsg = e.message;
      if (errorMsg === 'abort' || axios.isCancel(e)) {
        // Remove the file from the array - don't need to send an error
        const { abortFile } = this.props;
        abortFile(index);
      } else {
        if (!e.status) {
          errorMsg = 'timeout';
        }
        /**
         * In the event of no network connectivity - axios sends a string and not an object.
         * The !e.status check will be hit for network connectivity issues.
         *
         * See: https://github.com/axios/axios/issues/383
         */
        addFileError(errorMsg, index);
        this.setState({ isUploading: false });
      }
    }
  };

  /**
   * We need to do this GDSFP and SCU logic in order to re-render if the user
   * uses the select checkbox, otherwise avoid re-renders of these files at all costs!
   */
  static getDerivedStateFromProps(props, state) {
    const { imageUploaded, imageUploadedSrc } = props;

    // Re-sequencing changes image src of the files but nothing else
    if (imageUploadedSrc !== state.imageUploadedSrc) {
      return { imageUploadedSrc };
    }

    // If not a resequence and image already uploaded - don't worry
    if (imageUploaded) {
      return null;
    }

    const { file, index } = props;
    const stateObj = {};

    // Needed in the event that the user deletes a file which errored
    if (file.name !== state.file.name) {
      stateObj.file = file;
    }

    const hasError = get(file, 'hasError', null);
    if (hasError !== state.hasError) {
      // Needed for the case of a file timeout
      stateObj.hasError = hasError;
    }

    if (index !== state.index) {
      stateObj.index = index;
    }

    return stateObj;
  }

  /**
   * Only re-render if the individual file state changes (during upload),
   * or if the user clicks the checkbox
   */
  shouldComponentUpdate(nextProps, nextState) {
    return !isEqual(this.state, nextState);
  }

  // A file must follow the 1000x1000 minimum pixel requirement

  //  allow but show warning
  //  Moved into componemntDidMount section

  // prevent
  testImageDimensions = () => new Promise((resolve, reject) => {
    const { addFileError, file, index } = this.props;
    const imageTest = new Image();
    imageTest.onload = () => {
      if (imageTest.naturalWidth < 1000 || imageTest.naturalHeight < 1000) {
        URL.revokeObjectURL(imageTest.src);
        addFileError('pixel', index);
        reject(new Error('pixel'));
      } else {
        URL.revokeObjectURL(imageTest.src);
        resolve();
      }
    };

    imageTest.onerror = () => {
      URL.revokeObjectURL(imageTest.src);
      const isSafari = navigator.userAgent.toLowerCase().indexOf('safari/') > -1;
      if (isSafari) {
        addFileError('safari', index);
        reject(new Error('safari'));
      } else {
        addFileError('default', index);
        reject(new Error('default'));
      }
      this.setState({ imageThumbnail: null });
    };

    const imageThumbnail = URL.createObjectURL(file);
    imageTest.src = imageThumbnail;
    // Combine the upload 'start' here to reduce the total number of renders
    this.setState({ isUploading: true, imageThumbnail });
  });

  /**
   * Making this a function alleviates some issues with executing the three requests in a
   * synchronous manner.  We need to wait for the PUT request to complete before making the
   * request to CPS.
   *
   * This function solely exists to wrap the XMLHttpRequest functionality into a Promise, and
   * once the Promise resolves - we can then call the CPS request.
   */
  handlePutRequest = (putUrl, imageSeq, lotExt, lotNum) => new Promise((resolve, reject) => {
    /**
       * Using the XMLHttpRequest object, we gain more control over the request, which
       * allows us to do things such as control the timeout value, tap into the onprogress
       * event (create a progress bar)
       */
    const { file } = this.props;
    this.xhr = new XMLHttpRequest();

    // Timeout after 5 minutes
    this.xhr.timeout = 1000 * 60 * 5;
    this.xhr.open('PUT', putUrl);

    this.xhr.setRequestHeader('Content-Type', file.type);

    /**
     * WARNING!!! When doing this as a PUT request, this is xhr.upload.onprogress
     * NOT xhr.onprogress!!!
     */
    this.xhr.upload.onprogress = progressEvent => {
      const { loaded, total } = progressEvent;
      const progressPct = Math.round((loaded / total) * 100);
      this.setState({ progress: progressPct });
    };

    this.xhr.onabort = () => reject(new Error('abort'));

    this.xhr.ontimeout = () => {
      if (this.remainingAwsRetries > 0) {
        this.remainingAwsRetries -= 1;
        setTimeout(() => this.uploadFile(imageSeq, lotExt, lotNum), 3000);
      } else {
        const { addFileError, index } = this.props;
        addFileError('timeout', index);
        this.setState({ isUploading: false });
      }
    };

    this.xhr.onload = () => resolve();
    this.xhr.send(file);
  });

  // Cancel the XHR / CPS requests if they are still in progress
  cancelUpload = () => {
    if (this.cancelToken) {
      this.cancelToken.cancel();
    }

    if (this.xhr) {
      this.xhr.abort();
    }
  };

  render() {
    const { abortFile, handleDeleteFile, imageUploaded } = this.props;
    const {
      file,
      hasError,
      imageS3URL,
      imageThumbnail,
      imageUploadedSrc,
      index,
      isUploading,
      progress,
      showSizeWarning
    } = this.state;

    let imageSrc = null;
    const errorCode = get(file, 'error.code', null);
    if (imageUploaded && imageUploadedSrc) {
      imageSrc = imageUploadedSrc;
    } else if (errorCode === 'type' || errorCode === 'timeout') {
      /**
       * For invalid file types - show the no image placeholder
       * In the event of timeouts - theres a potential for a LOT
       * of images to reside in memory - so just show the placeholder
       */
      imageSrc = NO_IMAGE;
    } else if (imageS3URL) {
      /**
       * Once we have image thumbnail on S3 - change the imageSrc to point to S3,
       * and revokeObjectUrl to reclaim image memory
       */
      imageSrc = imageS3URL;
    } else {
      /**
       * When we initially render - this lets us use the thumbnail
       * we loaded when testing image dimensions
       */
      imageSrc = imageThumbnail || NO_IMAGE;
    }

    // Set the appropriate on click handler for the cancel upload / delete button
    let cancelDeleteOnClick = null;
    if (progress === 0) {
      cancelDeleteOnClick = () => abortFile(index);
    } else if (isUploading) {
      cancelDeleteOnClick = () => this.cancelUpload(index);
    } else {
      cancelDeleteOnClick = () => handleDeleteFile(index);
    }

    /**
     * Since the sortable container must be wrapped in a containing element,
     * we need to separate un-sortable files.
     *
     * First we load a row of errored files
     * Second we load the row of uploaded files
     * Finally we load the row of files which are currently uploading
     *
     * See AddLotImageContainer.js
     */

    // Has to be separated out from the sortable container
    if (hasError) {
      return (
        <LotImageErrored
          handleDeleteFile={abortFile}
          errorDescription={file.error.description}
          fileName={file.name}
          imageSrc={imageSrc}
          index={index}
        />
      );
    }

    // All uploaded images which are now sortable
    if (imageUploaded) {
      // (dyouberg) Index is required by react-sortable-hoc but is used internally
      return (
        <SortableImage
          handleDeleteFile={handleDeleteFile}
          image={imageSrc}
          imageNumber={index}
          index={index}
        />
      );
    }

    // The images which are still uploading...
    return (
      <LotImageUploading
        cancelDeleteOnClick={cancelDeleteOnClick}
        fileName={file.name}
        imageSrc={imageSrc}
        progress={progress}
      />
    );
  }
}

export default AddLotImage;
